import React, { useEffect, useRef } from 'react';
import { useOnScreen } from '../../hooks/useOnScreen';
import { ifEnter } from '../../util';
import { useSetModalState } from '../../hooks/actions/useModalState';
import { ModalToggleState } from '../../models/actionStates';
import classnames from 'classnames';
import styles from './ImageLoader.module.scss';
import { ZoomWrapper } from './ZoomWrapper';

export interface ImageLoaderProps {
  src: string;
  lazy?: boolean;
  onLoad?: (img: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  className?: string;
  style?: React.CSSProperties;
  loadingClassName?: string;
  loadedClassName?: string;
  width?: string | number;
  height?: string | number;
  alt: string;
  shouldUseDynamicDimensions?: boolean;
  allowClickToZoom?: boolean;
}
export const blankImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAoMBgDTD2qgAAAAASUVORK5CYII=';

export const ImageLoader: React.FC<ImageLoaderProps> = ({
  src,
  onLoad,
  className,
  style,
  loadingClassName,
  loadedClassName,
  width,
  height,
  allowClickToZoom,
  lazy = false,
  alt = '',
  shouldUseDynamicDimensions = false,
}) => {
  const [loaded, setLoaded] = React.useState<boolean>(false);
  const [hasIntersectionObserver, setHasIntersectionObserver] = React.useState<boolean>(true);
  useEffect(() => {
    setHasIntersectionObserver(!!window?.IntersectionObserver);
  }, []);

  const ref = useRef<HTMLImageElement>(null);
  const isNearViewport = useOnScreen(ref, '200px');
  const imageReadyToLoad = lazy && hasIntersectionObserver ? isNearViewport : true;
  // if (!loaded && imageReadyToLoad) console.log('Loading image', src);
  const onLoadFn = (img: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setLoaded(true);
    onLoad?.(img);
  };

  const combinedClassName = classnames(styles.imageLoader, {
    [className ?? '']: !!className,
    [styles.loaded]: loaded,
    [loadedClassName ?? '']: loaded,
    [styles.loading]: !loaded,
    [loadingClassName ?? '']: !loaded,
  });

  const useHeight = height ? height : shouldUseDynamicDimensions ? ref?.current?.getBoundingClientRect().height : undefined;
  const useWidth = width ? width : shouldUseDynamicDimensions ? ref?.current?.getBoundingClientRect().width : undefined;

  const image =
    loaded || imageReadyToLoad ? (
      <img src={src} style={style} className={combinedClassName} width={useWidth} height={useHeight} onLoad={onLoadFn} ref={ref} alt={alt} />
    ) : (
      <img src={blankImage} style={style} className={combinedClassName} width={useWidth} height={useHeight} ref={ref} alt="loading" />
    );

  return allowClickToZoom ? (
    <ZoomWrapper width={useWidth} height={useHeight} src={src}>
      {image}
    </ZoomWrapper>
  ) : (
    image
  );
};

export default ImageLoader;
